import { useState, useEffect, useRef, useCallback, type KeyboardEvent as ReactKeyboardEvent , type SyntheticEvent as ReactSyntheticEvent } from 'react'
import { useAuth } from '../context/AuthContext'
import { SendIcon, UserIcon, FileIcon, ImageIcon, AudioIcon } from '../icons'
import { linkifyText } from '../utils/linkify'
import { attachmentIsImage, attachmentIsAudio } from '../utils/attachments'
import type { ChannelMessage } from '../types'
import { API_URL, getAuthHeaders } from '../api'


const CDN_URL = 'https://cdn.colossus7.com'
const mediaUrl = (url: string | undefined) => {
  if (!url) return ''
  if (url.startsWith('/api/media/')) return CDN_URL + '/' + url.slice('/api/media/'.length)
  if (url.startsWith('/api/')) return API_URL + url
  return url
}

export function WhatsAppWidgetView({ channelId, contactId }: { channelId: string; contactId: string }) {
  const { token } = useAuth()
  const [messages, setMessages] = useState<ChannelMessage[]>([])
  const [input, setInput] = useState('')
  const [loading, setLoading] = useState(true)
  const messagesEndRef = useRef<HTMLDivElement | null>(null)
  const authHeaders = getAuthHeaders()

  const loadMessages = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/channel/messages?channel_id=${channelId}&contact_id=${contactId}`, { headers: authHeaders })
      const data = await res.json()
      setMessages(data.messages || [])
      // Mark contact as read (reset unread_count on backend)
      fetch(`${API_URL}/api/channel/contact/read`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({ contact_id: contactId }),
      }).catch(() => {})
    } catch {
      setMessages([])
    } finally {
      setLoading(false)
    }
  }, [channelId, contactId, token])

  useEffect(() => {
    loadMessages()
    const interval = setInterval(loadMessages, 8000)
    return () => clearInterval(interval)
  }, [loadMessages])

  // Listen for real-time channel notifications
  useEffect(() => {
    const handleNotification = (e: Event) => {
      const notif = (e as CustomEvent).detail
      if (notif.type === 'channel_message' && notif.channel_id === channelId && notif.contact_id === contactId) {
        loadMessages()
      }
    }
    window.addEventListener('dm-notification', handleNotification)
    return () => window.removeEventListener('dm-notification', handleNotification)
  }, [channelId, contactId, loadMessages])

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'auto' })
  }, [messages])

  const handleSend = async (e: ReactSyntheticEvent) => {
    e.preventDefault()
    const text = input.trim()
    if (!text || !channelId || !contactId) {
      console.warn('WhatsApp widget: missing data', { text, channelId, contactId })
      return
    }
    setInput('')

    const optimistic = {
      id: crypto.randomUUID(),
      direction: 'out',
      content: text,
      created_at: new Date().toISOString(),
    }
    setMessages(prev => [...prev, optimistic])

    const url = `${API_URL}/api/channel/messages?channel_id=${channelId}&contact_id=${contactId}`
    console.log('WhatsApp widget: sending', { url, text, hasAuth: !!authHeaders['Authorization'] })

    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({ content: text }),
      })
      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        console.error('WhatsApp widget send error:', res.status, err)
        setMessages(prev => prev.filter(m => m.id !== optimistic.id))
      } else {
        loadMessages()
      }
    } catch (err: unknown) {
      console.error('WhatsApp widget send fetch error:', err)
      setMessages(prev => prev.filter(m => m.id !== optimistic.id))
    }
  }

  const handleKeyDown = (e: ReactKeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend(e)
    }
  }

  const formatTime = (ts: string | number | Date | undefined) => {
    if (!ts) return ''
    const d = new Date(ts)
    return d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
  }

  if (loading) {
    return <div className="wa-widget-loading">Carregando...</div>
  }

  return (
    <div className="wa-widget-view">
      <div className="wa-widget-messages">
        {messages.length === 0 ? (
          <div className="wa-widget-empty">Nenhuma mensagem ainda.</div>
        ) : (
          messages.map(m => (
            <div key={m.id} className={`channel-msg ${m.direction === 'out' ? 'out' : 'in'}`}>
              <div className="channel-msg-bubble">
                {m.attachment_url && attachmentIsImage(m.attachment_type) && (
                  <a href={mediaUrl(m.attachment_url)} target="_blank" rel="noreferrer">
                    <img src={mediaUrl(m.attachment_url)} alt={m.attachment_name} className="channel-msg-img" />
                  </a>
                )}
                {m.attachment_url && attachmentIsAudio(m.attachment_type) && (
                  <div className="channel-msg-audio">
                    <AudioIcon size={16} />
                    <audio controls src={mediaUrl(m.attachment_url)} className="channel-audio-element" />
                  </div>
                )}
                {m.attachment_url && !attachmentIsImage(m.attachment_type) && !attachmentIsAudio(m.attachment_type) && (
                  <a href={mediaUrl(m.attachment_url)} target="_blank" rel="noreferrer" className="channel-msg-file">
                    <FileIcon size={16} /> {m.attachment_name || 'Arquivo'}
                  </a>
                )}
                {m.content && <div className="channel-msg-text">{linkifyText(m.content)}</div>}
                <div className="channel-msg-time">{formatTime(m.created_at)}</div>
              </div>
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      <form className="wa-widget-input-row" onSubmit={handleSend}>
        <textarea
          className="wa-widget-input"
          placeholder="Mensagem..."
          value={input}
          onChange={e => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          rows={1}
        />
        <button type="submit" className="btn-primary" disabled={!input.trim()}>
          <SendIcon size={16} />
        </button>
      </form>
    </div>
  )
}
